import { useState, useCallback } from 'react';
import { ScanFace, Shield, AlertTriangle, CheckCircle, Loader2, X } from 'lucide-react';
import { Modal, Button } from '../ui';
import { CameraCapture } from '../identity/CameraCapture';
import { api } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

export type FacialVerificationOperationType = 
  | 'PIX_SEND' 
  | 'TRANSFER' 
  | 'WITHDRAWAL' 
  | 'TOKEN_PURCHASE' 
  | 'CONTRACT_SIGN';

interface FacialVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (sessionId: string) => void;
  operationType: FacialVerificationOperationType;
  operationValue?: number;
  sessionId?: string;
  reason?: string;
}

const OPERATION_LABELS: Record<FacialVerificationOperationType, string> = {
  PIX_SEND: 'Envio de PIX',
  TRANSFER: 'Transferência',
  WITHDRAWAL: 'Saque',
  TOKEN_PURCHASE: 'Compra de Token',
  CONTRACT_SIGN: 'Assinatura de Contrato',
};

type VerificationStep = 'info' | 'capture' | 'processing' | 'success' | 'error';

export function FacialVerificationModal({
  isOpen,
  onClose,
  onVerified,
  operationType,
  operationValue,
  sessionId,
  reason,
}: FacialVerificationModalProps) {
  const [step, setStep] = useState<VerificationStep>('info');
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const handleStartCapture = () => {
    setStep('capture');
    setError(null);
  };

  const handleCapture = useCallback(async (imageData: string) => {
    setCapturedImage(imageData);
    setStep('processing');
    setError(null);

    try {
      // Enviar imagem para verificação
      const response = await api.post('/identity/verify-face', {
        sessionId,
        imageData,
        operationType,
      });

      if (response.data.verified) {
        setStep('success');
        // Aguardar um momento para mostrar sucesso antes de fechar
        setTimeout(() => {
          onVerified(response.data.sessionId || sessionId || '');
        }, 1500);
      } else {
        setError(response.data.message || 'Verificação facial falhou. Tente novamente.');
        setStep('error');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao processar verificação facial';
      setError(errorMessage);
      setStep('error');
    }
  }, [sessionId, operationType, onVerified]);

  const handleRetry = () => {
    setCapturedImage(null);
    setError(null);
    setStep('capture');
  };

  const handleClose = () => {
    setStep('info');
    setCapturedImage(null);
    setError(null);
    onClose();
  };

  const renderContent = () => {
    switch (step) {
      case 'info':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-500/20 flex items-center justify-center">
              <ScanFace className="w-10 h-10 text-primary-400" />
            </div>
            
            <h3 className="text-xl font-semibold text-surface-100 mb-2">
              Verificação Facial Necessária
            </h3>
            
            <p className="text-surface-400 mb-4">
              Para sua segurança, precisamos verificar sua identidade antes de prosseguir com esta operação.
            </p>

            <div className="bg-surface-800 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="font-medium text-surface-200">Detalhes da Operação</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-400">Tipo:</span>
                  <span className="text-surface-200">{OPERATION_LABELS[operationType]}</span>
                </div>
                {operationValue !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Valor:</span>
                    <span className="text-surface-200 font-medium">{formatCurrency(operationValue)}</span>
                  </div>
                )}
                {reason && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Motivo:</span>
                    <span className="text-surface-200">{reason}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-6 text-left">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-300">
                  <p className="font-medium mb-1">Dicas para uma boa captura:</p>
                  <ul className="list-disc list-inside text-yellow-300/80 space-y-1">
                    <li>Posicione seu rosto no centro da tela</li>
                    <li>Certifique-se de ter boa iluminação</li>
                    <li>Remova óculos escuros ou acessórios que cubram o rosto</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleStartCapture} className="flex-1">
                <ScanFace className="w-4 h-4 mr-2" />
                Iniciar Verificação
              </Button>
            </div>
          </div>
        );

      case 'capture':
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-surface-100">
                Captura Facial
              </h3>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-surface-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-surface-400" />
              </button>
            </div>
            
            <CameraCapture
              onCapture={handleCapture}
              onError={(err) => {
                setError(err);
                setStep('error');
              }}
              facingMode="user"
              showPreview={false}
              autoStart={true}
              className="mb-4"
            />
            
            <p className="text-sm text-surface-400 text-center">
              Posicione seu rosto no centro e clique no botão para capturar
            </p>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            </div>
            
            <h3 className="text-xl font-semibold text-surface-100 mb-2">
              Processando Verificação
            </h3>
            
            <p className="text-surface-400">
              Aguarde enquanto verificamos sua identidade...
            </p>

            {capturedImage && (
              <div className="mt-6 max-w-xs mx-auto">
                <img
                  src={capturedImage}
                  alt="Imagem capturada"
                  className="w-full rounded-lg opacity-50"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            
            <h3 className="text-xl font-semibold text-surface-100 mb-2">
              Verificação Concluída
            </h3>
            
            <p className="text-surface-400">
              Sua identidade foi verificada com sucesso. A operação será processada.
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            
            <h3 className="text-xl font-semibold text-surface-100 mb-2">
              Verificação Falhou
            </h3>
            
            <p className="text-surface-400 mb-6">
              {error || 'Não foi possível verificar sua identidade. Por favor, tente novamente.'}
            </p>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleRetry} className="flex-1">
                Tentar Novamente
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 'processing' || step === 'success' ? () => {} : handleClose}
      title=""
      size="md"
    >
      {renderContent()}
    </Modal>
  );
}

export default FacialVerificationModal;
